import React from 'react';
import _ from 'lodash';
import { Dialog } from '@mui/material';
import './../../../assets/style/CallBookingCalendar.css';
import CallBookingCalendar from './CallBookingCalendar';
import CommonModalHeader from '../../../components/DialogUi/CommonModalHeader';
import { MOBILE_SX, useIsMobileApp } from '../../../mobile/mobileApp';

/**
 * Phone: the booking form is a full-screen sheet. The paper scrolls, the title
 * bar stays on top, and the form, the month and the time slots stack in one
 * column (custom.scss lays them out in columns for desktop).
 */
const phoneBookingSx = {
  [MOBILE_SX]: {
    '& .CallBookingDialogPaper': {
      width: '100% !important',
      maxWidth: 'none !important',
      borderRadius: '0 !important',
      overflowY: 'auto',
      background: '#0F0F0F !important'
    },
    '& .CallBookingWrapper': { height: 'auto', minHeight: '100%', borderRadius: 0, outline: 'none !important' },
    '& .CallBookingWrapper .chat-dialog-header': {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      background: '#0F0F0F !important',
      padding: '12px 8px 12px 16px',
      gap: '8px',
      alignItems: 'center'
    },
    // A long "Book time with <name>" wraps beside the close button instead of running under it.
    '& .CallBookingWrapper .chat-dialog-header > :not(:last-child)': { minWidth: 0 },
    '& .CallBookingWrapper .chat-dialog-header > :last-child': { flexShrink: 0 },
    '& .CallBookingWrapper .chat-dialog-title': {
      flex: '1 1 auto',
      minWidth: 0,
      overflowWrap: 'anywhere',
      lineHeight: 1.3
    },
    '& .CallBookingWrapper .chat-dialog-close': { width: '40px', height: '40px' },
    '& .CallBookingContent': { padding: '16px !important', flex: '1 0 auto' },
    '& .CallBookingCalendarGrid': { gridTemplateColumns: 'minmax(0, 1fr) !important', gap: '24px' },
    '& .CallBookingCalendarRightGrid': { gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px' },
    '& .CallBookingCalendarCard': { padding: '12px !important', gap: '8px' },
    '& .CallBookingTimeSlotsCard': { padding: '12px !important', maxHeight: 'none' },
    '& .CallBookingTimeSlotsCard .time-slots-container': { maxHeight: '240px !important', gap: '8px' },
    '& .CallBookingTimeSlotsCard .time-slots-container .checkedBoxs': {
      minHeight: '40px',
      fontSize: '14px !important'
    },
    // Cancel and Confirm stay pinned at the bottom of the sheet, side by side.
    '& .CallBookingWrapper .MuiDialogActions-root': {
      position: 'sticky',
      bottom: 0,
      zIndex: 2,
      padding: '12px 16px !important',
      gap: '12px',
      borderTop: '1px solid #2A2A2A'
    },
    '& .CallBookingWrapper .MuiDialogActions-root > .MuiButton-root': {
      flex: 1,
      width: 'auto',
      minHeight: '44px',
      margin: 0
    }
  }
};

interface CallBookingModalProps {
  title?: string;
  handleCloseModal: () => void;
  openFlag: boolean;
  chatProfileName: string;
  chatType: string;
  threadParticipantsUsers: any[];
  pageName: string;
  bookedCallDetails: any;
  isCallEditing?: boolean;
  objectiveDetails?: any;
  onMeetingScheduled?: (meetingDetails: any) => void;
  handleCloseRecruitMentor?: () => void;
  /** From Chat: other user id and role from getCommunityGroupsUsersListNew (for mentee-mentor flow and overlapping objectives) */
  otherParticipantId?: string;
  otherParticipantRole?: string;
}

const CallBookingModal: React.FC<CallBookingModalProps> = ({
  title,
  handleCloseModal,
  openFlag,
  chatProfileName,
  threadParticipantsUsers,
  chatType,
  pageName,
  bookedCallDetails,
  isCallEditing = false,
  objectiveDetails,
  onMeetingScheduled,
  handleCloseRecruitMentor,
  otherParticipantId,
  otherParticipantRole
}) => {
  const isMobileApp = useIsMobileApp();
  return (
    <Dialog
      open={openFlag}
      onClose={() => handleCloseModal()}
      scroll={isMobileApp ? 'paper' : 'body'}
      fullScreen={isMobileApp}
      sx={phoneBookingSx}
      data-testid="call-booking-modal"
      PaperProps={{ className: 'CallBookingDialogPaper' }}
    >
      <div className="CallBookingWrapper">
        <CommonModalHeader
          handleCloseModal={handleCloseModal}
          title={pageName === 'Calendar' ? 'Schedule New Meeting' : `Book time with ${chatProfileName}`}
        />

        <div className="CallBookingContent">
          <CallBookingCalendar
            title={title}
            onClose={() => handleCloseModal()}
            channelNaame={chatProfileName}
            threadParticipantsUsers={threadParticipantsUsers}
            chatType={chatType}
            pageName={pageName}
            bookedCallDetails={bookedCallDetails}
            isCallEditing={isCallEditing}
            objectiveDetails={objectiveDetails}
            onMeetingScheduled={onMeetingScheduled}
            handleCloseRecruitMentor={handleCloseRecruitMentor}
            otherParticipantId={otherParticipantId}
            otherParticipantRole={otherParticipantRole}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default CallBookingModal;
